/**
 * The drops list's window into the Drop Generator's creative fill: a
 * progress strip while AI texts/senders are being written, and a hook the
 * list uses to mark still-empty generator rows as "generating…". Every
 * time the filled count moves, the drops list is invalidated so creative
 * pops in row by row instead of appearing silently later. The completion
 * toast fires once on the FILLING -> terminal edge (this page has its own
 * watcher — the board indicator unmounts when the operator navigates
 * here). Hidden entirely without drop_generator.view or on the pinned
 * page's sentinel country.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { useCreativeFillStatus } from '@/features/drop-generator/useDropGenerator';
import { invalidatePreparedDrops } from '@/hooks/smswiser-campaign-workspace/invalidate-prepared-drops';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

const doneSummary = (filled: number, failed: number, skipped: number): string => {
  const parts = [`${filled} drafts have their creative`];
  if (failed > 0) parts.push(`${failed} failed — see the Automation board`);
  if (skipped > 0)
    parts.push(`${skipped} container${skipped === 1 ? '' : 's'} skipped`);
  return parts.join(' · ');
};

/** True while the generator is writing creative for this country. */
export function useCreativeFilling(countryKey: string): boolean {
  const { hasPermission } = useAuth();
  const enabled =
    countryKey !== 'pinned-prepared' && hasPermission('drop_generator.view');
  const status = useCreativeFillStatus(enabled ? countryKey : null);
  return status.data?.status === 'FILLING';
}

export const CreativeFillBanner: FC<{ countryKey: string }> = ({
  countryKey,
}) => {
  const { hasPermission } = useAuth();
  const enabled =
    countryKey !== 'pinned-prepared' && hasPermission('drop_generator.view');
  const status = useCreativeFillStatus(enabled ? countryKey : null);
  const queryClient = useQueryClient();
  const { success, warning, error } = useToast();
  const lastFilled = useRef(0);
  const wasFilling = useRef(false);
  const s = status.data;

  useEffect(() => {
    if (!s) return;
    if (s.status === 'FILLING') {
      wasFilling.current = true;
      if (s.filled !== lastFilled.current) {
        lastFilled.current = s.filled;
        if (s.filled > 0) invalidatePreparedDrops(queryClient, countryKey);
      }
      return;
    }
    if (!wasFilling.current) return;
    wasFilling.current = false;
    invalidatePreparedDrops(queryClient, countryKey);
    if (s.status === 'DONE') {
      const failed = s.report?.reduce((n, r) => n + r.failed, 0) ?? 0;
      const skipped = s.report?.filter((r) => r.skippedReason).length ?? 0;
      const toast = failed > 0 ? warning : success;
      toast('Texts & senders ready', doneSummary(s.filled, failed, skipped));
    } else if (s.status === 'FAILED')
      error('Creative fill failed', s.error ?? undefined);
  }, [s, queryClient, countryKey, success, warning, error]);

  if (s?.status !== 'FILLING') return null;
  const pct = s.total > 0 ? Math.max(2, (s.filled / s.total) * 100) : 2;
  return (
    <div className="border-b border-border/60 bg-primary/5 px-3 py-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        <span className="font-medium text-foreground">
          Generating texts &amp; senders…
        </span>
        <span>
          {s.filled} of {s.total} drafts ready — rows fill in as the AI
          finishes them
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-border/60">
        <div
          className="h-full rounded bg-primary transition-[width] duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
